import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
} from "@mui/material";
import { Check, Close, ErrorOutline } from "@mui/icons-material";
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import axios from "axios";
import {
  generateTwilioApplicationDetails,
  getOrganizationDetail,
  getOrganizationTwilio,
  queryActivity,
  setTrialEnd,
} from "../../api/adminApi";
import ActivityTimeline from "./ActivityTimeline";
import AddActivityNoteModal from "./AddActivityNoteModal";
import ImpersonateModal from "./ImpersonateModal";
import LockoutOverrideModal from "./LockoutOverrideModal";
import RouteDataModal from "./RouteDataModal";
import UpdateVoiceUrlModal from "./UpdateVoiceUrlModal";

function unwrap(value) {
  if (value == null) return null;
  if (typeof value !== "object") return value;
  if (value.S != null) return value.S;
  if (value.N != null) return Number(value.N);
  if (value.BOOL != null) return value.BOOL;
  return value;
}

function pickOrgField(org, ...keys) {
  if (!org) return null;
  for (const key of keys) {
    const direct = org[key];
    if (direct != null && typeof direct !== "object") return direct;
    const unwrapped = unwrap(direct);
    if (unwrapped != null && unwrapped !== direct) return unwrapped;
    if (org[key]?.S != null) return org[key].S;
    if (org[key]?.N != null) return Number(org[key].N);
  }
  return null;
}

/** Resolve owner userId/email from org.users (Dynamo or plain). */
function findOwnerFromOrganization(org) {
  if (!org) return { userId: null, email: null };
  const rawUsers = org.users?.L || org.users || [];
  const entries = (Array.isArray(rawUsers) ? rawUsers : [])
    .map((u) => u?.M || u)
    .filter(Boolean);

  const owner =
    entries.find((u) => String(unwrap(u.type) || "").toLowerCase() === "owner") ||
    entries[0] ||
    null;

  if (!owner) return { userId: null, email: null };
  return {
    userId: unwrap(owner.userId) || unwrap(owner.user_id) || null,
    email: unwrap(owner.email) || null,
  };
}

function readLockoutOverride(org) {
  const raw = org?.lockout_override?.M || org?.lockout_override;
  if (!raw || typeof raw !== "object") return null;
  const isActive = coerceBoolean(raw.is_active) === true;
  const expiration = unwrap(raw.expiration);
  const expNum = expiration == null ? null : Number(expiration);
  return {
    isActive,
    expiration: Number.isFinite(expNum) ? expNum : null,
    reason: unwrap(raw.reason) || null,
    lastUpdatedby: unwrap(raw.lastUpdatedby) || null,
  };
}

function lockoutOverrideLabel(override) {
  if (!override?.isActive || override.expiration == null) return null;
  const ms = override.expiration * 1000;
  if (ms <= Date.now()) return "Expired";
  return `Active until ${new Date(ms).toLocaleString()}`;
}

function formatPeriodEnd(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    const ms = n < 1e12 ? n * 1000 : n;
    return new Date(ms).toLocaleDateString();
  }
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
}

const ACTIVITY_PAGE_SIZE = 50;

function extractActivityPage(data) {
  if (!data) return { items: [], cursor: null, hasMore: false };
  const nested =
    data.activity && typeof data.activity === "object" && !Array.isArray(data.activity)
      ? data.activity
      : null;
  const items = Array.isArray(data.items)
    ? data.items
    : Array.isArray(nested?.items)
      ? nested.items
      : Array.isArray(data.activity)
        ? data.activity
        : [];
  const source = nested || data;
  const cursor =
    source.lastEvaluatedKey ??
    source.exclusiveStartKey ??
    source.nextExclusiveStartKey ??
    source.cursor ??
    null;
  const hasMoreFlag = source.hasMore ?? source.has_more;
  const hasMore =
    typeof hasMoreFlag === "boolean"
      ? hasMoreFlag
      : Boolean(cursor) || items.length >= ACTIVITY_PAGE_SIZE;
  return { items, cursor, hasMore };
}

function activityItemKey(item) {
  return (
    item?.eventId ||
    item?.sk ||
    `${item?.timestamp}|${item?.summary || ""}|${item?.body || ""}`
  );
}

function coerceBoolean(value) {
  if (value == null) return null;
  if (typeof value === "boolean") return value;
  if (typeof value === "object") {
    if (value.BOOL != null) return Boolean(value.BOOL);
    if (value.S != null) return coerceBoolean(value.S);
    if (value.N != null) return coerceBoolean(value.N);
  }
  if (typeof value === "string") {
    const s = value.trim().toLowerCase();
    if (s === "true" || s === "1" || s === "yes") return true;
    if (s === "false" || s === "0" || s === "no") return false;
    return null;
  }
  if (typeof value === "number") return value !== 0;
  return null;
}

function SidValidityIcon({ valid }) {
  if (valid !== true && valid !== false) return null;
  const title =
    valid === true
      ? "This SID is valid in the Twilio account"
      : "This SID is not present in the Twilio account";
  return (
    <MuiTooltip title={title} arrow placement="top">
      <span className="inline-flex shrink-0 mt-0.5 cursor-default" aria-label={title}>
        {valid === true ? (
          <Check sx={{ fontSize: 16, color: "#16A34A" }} />
        ) : (
          <Close sx={{ fontSize: 16, color: "#DC2626" }} />
        )}
      </span>
    </MuiTooltip>
  );
}

function MetaItem({ label, value, sidValid }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
        {label}
      </div>
      <div className="text-[13px] text-gray-800 mt-0.5 break-all flex items-start gap-1">
        <span>{value == null || value === "" ? "—" : String(value)}</span>
        <SidValidityIcon valid={sidValid} />
      </div>
    </div>
  );
}

function readTrialExpiresAt(org) {
  const raw =
    unwrap(org?.subscription_details?.M?.trial_expires_at) ??
    unwrap(org?.subscription_details?.trial_expires_at);
  const n = raw == null || raw === "" ? NaN : Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function toDateInputValue(seconds) {
  if (seconds == null) return "";
  const d = new Date(seconds * 1000);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** Trial ends at 23:59:59 local time on the picked day. */
function fromDateInputValue(value) {
  const [y, m, d] = (value || "").split("-").map(Number);
  if (!y || !m || !d || y < 2000 || y > 2100) return null;
  return Math.floor(new Date(y, m - 1, d, 23, 59, 59).getTime() / 1000);
}

function TrialEndItem({ orgId, trialExpiresAt, isPaid, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const inputRef = useRef(null);

  const startEditing = () => {
    setDraft(toDateInputValue(trialExpiresAt));
    setErr(null);
    setEditing(true);
  };

  const cancel = () => {
    setEditing(false);
    setErr(null);
  };

  useEffect(() => {
    if (!editing) return;
    try {
      inputRef.current?.showPicker?.();
    } catch {
      // showPicker needs a user gesture in some browsers
    }
  }, [editing]);

  const save = async () => {
    if (saving) return;
    const next = fromDateInputValue(draft);
    if (next == null) {
      setErr("Enter a complete date (year 2000–2100)");
      return;
    }
    if (next === trialExpiresAt) {
      cancel();
      return;
    }
    if (
      next < Date.now() / 1000 &&
      !window.confirm(
        `${new Date(next * 1000).toLocaleDateString()} is in the past. Saving it will cancel the trial and lock the customer out immediately. Continue?`
      )
    ) {
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const data = await setTrialEnd({ orgId, trialExpiresAt: next });
      onSaved(data.trialExpiresAt, data.subStatus);
      setEditing(false);
    } catch (e) {
      setErr(e?.response?.data?.error || e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const label = trialExpiresAt
    ? new Date(trialExpiresAt * 1000).toLocaleDateString()
    : "—";

  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
        Trial end
      </div>
      <div className="text-[13px] text-gray-800 mt-0.5 flex items-center gap-1">
        {editing ? (
          <>
            <input
              type="date"
              autoFocus
              disabled={saving}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                } else if (e.key === "Escape") {
                  cancel();
                }
              }}
              ref={inputRef}
              className="border border-gray-300 rounded px-1 text-[13px]"
            />
            <button
              type="button"
              disabled={saving}
              onClick={save}
              className="text-[12px] text-[#2563EB] hover:underline disabled:opacity-50"
            >
              Save
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={cancel}
              className="text-[12px] text-gray-500 hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          </>
        ) : isPaid ? (
          <MuiTooltip title="Paid organizations have no trial" arrow placement="top">
            <span className="text-gray-400">{label}</span>
          </MuiTooltip>
        ) : (
          <button
            type="button"
            onClick={startEditing}
            className="text-left underline decoration-dotted underline-offset-2 hover:text-[#2563EB]"
          >
            {label}
          </button>
        )}
        {saving && <CircularProgress size={12} />}
      </div>
      {err && <div className="text-[11px] text-red-600 mt-0.5">{err}</div>}
    </div>
  );
}

const AdminOrgDetail = () => {
  const { orgId } = useParams();
  const location = useLocation();
  const navState = location.state || {};

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [users, setUsers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [callCounts, setCallCounts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activityCursor, setActivityCursor] = useState(null);
  const [activityHasMore, setActivityHasMore] = useState(false);
  const [activityLoadingMore, setActivityLoadingMore] = useState(false);
  const [listMeta, setListMeta] = useState({
    ownerEmail: navState.ownerEmail || null,
    ownerUserId: navState.ownerUserId || null,
    organizationName: navState.organizationName || null,
    subscriptionStatus: navState.subscriptionStatus || null,
    subscriptionEndDate: navState.subscriptionEndDate || null,
    mrr: navState.mrr ?? null,
    paymentMethodEnabled:
      navState.paymentMethodEnabled != null
        ? Boolean(navState.paymentMethodEnabled)
        : null,
    stripeCustomerId: navState.stripeCustomerId || null,
    stripeSubscriptionId: navState.stripeSubscriptionId || null,
  });

  const [twilio, setTwilio] = useState(null);
  const [twilioFetchMeta, setTwilioFetchMeta] = useState({
    accountFetchError: null,
    appSidError: null,
    apiSidError: null,
    numbersError: null,
  });
  const [twilioLoading, setTwilioLoading] = useState(false);
  const [twilioError, setTwilioError] = useState(null);
  const [twilioLoaded, setTwilioLoaded] = useState(false);
  const [genLoader, setGenLoader] = useState(false);
  const [genMsg, setGenMsg] = useState(null);

  const [noteOpen, setNoteOpen] = useState(false);
  const [impersonateOpen, setImpersonateOpen] = useState(false);
  const [lockoutOpen, setLockoutOpen] = useState(false);
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voicePrefill, setVoicePrefill] = useState({ phone: "", url: "" });
  const [callSeries, setCallSeries] = useState("total");
  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [orgDataOpen, setOrgDataOpen] = useState(false);

  const loadDetail = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getOrganizationDetail(orgId);
      const org = data.organization || null;
      const orgUsers = Array.isArray(data.users) ? data.users : [];
      setOrganization(org);
      setUsers(orgUsers);
      setRoutes(Array.isArray(data.routes) ? data.routes : []);
      setCallCounts(
        Array.isArray(data.callCountsLast14Days) ? data.callCountsLast14Days : []
      );
      const activityPage = extractActivityPage(data);
      setActivity(activityPage.items);
      setActivityCursor(activityPage.cursor);
      setActivityHasMore(activityPage.hasMore);

      const ownerFromUsers =
        orgUsers.find(
          (u) => String(u.userType || u.type || "").toLowerCase() === "owner"
        ) || orgUsers[0] || null;
      const ownerFromOrg = findOwnerFromOrganization(org);
      let ownerEmail =
        data.ownerEmail ||
        ownerFromUsers?.email ||
        pickOrgField(org, "ownerEmail", "owner_email") ||
        ownerFromOrg.email ||
        navState.ownerEmail ||
        null;
      const ownerUserId =
        data.ownerUserId ||
        ownerFromUsers?.userId ||
        pickOrgField(org, "ownerUserId", "owner_user_id", "ownerId") ||
        ownerFromOrg.userId ||
        navState.ownerUserId ||
        null;

      // Detail often has owner userId but not email — resolve for display.
      if (!ownerEmail && ownerUserId) {
        try {
          const userRes = await axios.post(
            process.env.REACT_APP_BACKEND_URL + "get-user-by-id",
            { userId: ownerUserId }
          );
          ownerEmail =
            unwrap(userRes?.data?.user?.email) ||
            userRes?.data?.user?.email ||
            null;
        } catch (err) {
          console.warn("[Admin] could not resolve owner email", err);
        }
      }

      setListMeta({
        ownerEmail,
        ownerUserId,
        organizationName:
          data.organizationName ||
          pickOrgField(org, "organizationName", "organization_name") ||
          navState.organizationName ||
          null,
        subscriptionStatus:
          data.subscriptionStatus ||
          pickOrgField(org, "subscriptionStatus", "subscription_status") ||
          unwrap(org?.subscription_details?.status) ||
          unwrap(org?.subscription_details?.M?.status) ||
          unwrap(org?.subscription_details?.M?.sub_status) ||
          navState.subscriptionStatus ||
          null,
        subscriptionEndDate:
          data.subscriptionEndDate ||
          pickOrgField(
            org,
            "subscriptionEndDate",
            "subscription_end_date",
            "current_period_end"
          ) ||
          unwrap(org?.subscription_details?.current_period_end) ||
          unwrap(org?.subscription_details?.M?.current_period_end) ||
          navState.subscriptionEndDate ||
          null,
        mrr:
          data.mrr ??
          unwrap(org?.subscription_details?.mrr) ??
          unwrap(org?.subscription_details?.M?.mrr) ??
          navState.mrr ??
          null,
        paymentMethodEnabled:
          coerceBoolean(data.paymentMethodEnabled) ??
          coerceBoolean(org?.paymentMethodEnabled) ??
          coerceBoolean(org?.payment_method_enabled) ??
          coerceBoolean(org?.subscription_details?.payment_method_enabled) ??
          coerceBoolean(org?.subscription_details?.M?.payment_method_enabled) ??
          (navState.paymentMethodEnabled != null
            ? Boolean(navState.paymentMethodEnabled)
            : null),
        stripeCustomerId:
          data.stripeCustomerId ||
          pickOrgField(org, "stripe_cust_id", "stripeCustomerId") ||
          navState.stripeCustomerId ||
          null,
        stripeSubscriptionId:
          data.stripeSubscriptionId ||
          pickOrgField(org, "stripe_sub", "stripeSubscriptionId") ||
          navState.stripeSubscriptionId ||
          null,
      });
    } catch (err) {
      console.error("[Admin] org detail failed", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Failed to load organization"
      );
    } finally {
      setLoading(false);
    }
    // navState is seed-only; orgId drives reloads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (loading) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await queryActivity({ orgId, limit: ACTIVITY_PAGE_SIZE });
        if (cancelled) return;
        const page = extractActivityPage(data);
        setActivity(page.items);
        setActivityCursor(page.cursor);
        setActivityHasMore(page.hasMore);
      } catch (err) {
        console.warn("[Admin] activity query failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, loading]);

  const refreshActivity = async () => {
    try {
      const data = await queryActivity({ orgId, limit: ACTIVITY_PAGE_SIZE });
      const page = extractActivityPage(data);
      setActivity(page.items);
      setActivityCursor(page.cursor);
      setActivityHasMore(page.hasMore);
    } catch {
      await loadDetail();
    }
  };

  const loadMoreActivity = async () => {
    if (activityLoadingMore || !activityHasMore) return;
    setActivityLoadingMore(true);
    try {
      const lastItem = activity[activity.length - 1];
      const data = await queryActivity({
        orgId,
        limit: ACTIVITY_PAGE_SIZE,
        exclusiveStartKey: activityCursor || undefined,
        ...(activityCursor || !lastItem?.timestamp
          ? {}
          : { to: lastItem.timestamp }),
      });
      const page = extractActivityPage(data);
      const seen = new Set(activity.map((item) => activityItemKey(item)));
      const appended = page.items.filter(
        (item) => !seen.has(activityItemKey(item))
      );
      setActivity((prev) => [...prev, ...appended]);
      setActivityCursor(page.cursor);
      setActivityHasMore(
        appended.length === 0 ? false : page.hasMore && appended.length > 0
      );
    } catch (err) {
      console.error("[Admin] load more activity failed", err);
      setActivityHasMore(false);
    } finally {
      setActivityLoadingMore(false);
    }
  };

  const loadTwilio = async () => {
    setTwilioLoading(true);
    setTwilioError(null);
    try {
      const data = await getOrganizationTwilio(orgId);
      setTwilio(data.twilio ?? data ?? null);
      setTwilioFetchMeta({
        accountFetchError: data.accountFetchError ?? null,
        appSidError: data.appSidError ?? null,
        apiSidError: data.apiSidError ?? null,
        numbersError: data.numbersError ?? null,
      });
      setTwilioLoaded(true);
    } catch (err) {
      setTwilioError(
        err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Failed to load Twilio details"
      );
    } finally {
      setTwilioLoading(false);
    }
  };

  const handleGenerateAppDetails = async () => {
    setGenLoader(true);
    setGenMsg(null);
    try {
      const orgName =
        listMeta?.organizationName ||
        pickOrgField(organization, "organization_name", "organizationName");
      const tz = pickOrgField(organization, "org_timezone", "orgTimezone");
      const twilioIntegration =
        organization?.twilio_integration?.M ||
        organization?.twilio_integration ||
        undefined;
      await generateTwilioApplicationDetails({
        orgId,
        org_id: orgId,
        organization_name: orgName || undefined,
        org_timezone: tz || undefined,
        twilio_integration: twilioIntegration,
      });
      setGenMsg("Application details generated");
      await loadTwilio();
    } catch (err) {
      setGenMsg(
        err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Generate failed"
      );
    } finally {
      setGenLoader(false);
    }
  };

  const CALL_SERIES = [
    { id: "total", label: "Total calls", color: "#605BFF" },
    { id: "inbound", label: "Inbound", color: "#8B5CF6" },
    { id: "outbound", label: "Outbound", color: "#F59E0B" },
  ];

  const chartData = useMemo(
    () =>
      (callCounts || []).map((row) => ({
        date: row.date,
        label: row.date
          ? new Date(row.date + "T12:00:00").toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
            })
          : "",
        total: row.total ?? (row.inbound || 0) + (row.outbound || 0),
        inbound: row.inbound ?? 0,
        outbound: row.outbound ?? 0,
      })),
    [callCounts]
  );

  const activeCallSeries =
    CALL_SERIES.find((s) => s.id === callSeries) || CALL_SERIES[0];
  const chartGradId = `adminCallsGrad-${activeCallSeries.id}`;

  const orgName =
    listMeta?.organizationName ||
    pickOrgField(organization, "organization_name", "organizationName") ||
    orgId;
  const ownerEmail = listMeta?.ownerEmail;
  const ownerUserId = listMeta?.ownerUserId;
  const stripeCustomerId = listMeta?.stripeCustomerId;

  const stripeUrl = stripeCustomerId
    ? `https://dashboard.stripe.com/acct_1QHrHUI7dE8EQ2hg/customers/${stripeCustomerId}`
    : null;
  const awsUrl = orgId
    ? `https://us-east-2.console.aws.amazon.com/dynamodbv2/home?region=us-east-2#edit-item?itemMode=2&pk=${encodeURIComponent(
        orgId
      )}&table=organizations`
    : null;
  const posthogUrl = orgId
    ? `https://us.posthog.com/project/114295/groups/0/${encodeURIComponent(orgId)}`
    : null;

  const twilioNumbers = Array.isArray(twilio?.numbers) ? twilio.numbers : [];

  const externalLinkSx = {
    textTransform: "none",
    border: "1px solid #E0E0E0",
    color: "#374151",
    borderRadius: "8px",
    px: 2.5,
    "&.Mui-disabled": { color: "#9CA3AF", borderColor: "#E5E7EB" },
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <CircularProgress size={32} sx={{ color: "#605BFF" }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 text-[13px] font-semibold">
        <ErrorOutline sx={{ fontSize: 16 }} /> {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
        <div>
          <h1 className="lg:text-[22px] text-[20px] font-bold text-gray-900">
            {orgName}
          </h1>
          <p className="text-[13px] text-gray-500 mt-1">{orgId}</p>
        </div>
        <div className="flex flex-wrap gap-2 self-start">
          <Button
            onClick={() => setOrgDataOpen(true)}
            sx={{
              textTransform: "none",
              border: "1px solid #E0E0E0",
              color: "#374151",
              borderRadius: "8px",
              px: 2.5,
            }}
          >
            View all details
          </Button>
          <Button
            component="a"
            href={stripeUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!stripeUrl}
            sx={externalLinkSx}
          >
            Stripe
          </Button>
          <Button
            component="a"
            href={awsUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!awsUrl}
            sx={externalLinkSx}
          >
            AWS
          </Button>
          <Button
            component="a"
            href={posthogUrl || undefined}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!posthogUrl}
            sx={externalLinkSx}
          >
            PostHog
          </Button>
          <Button
            onClick={() => setImpersonateOpen(true)}
            disabled={!ownerUserId && !ownerEmail}
            sx={{
              textTransform: "none",
              bgcolor: "#605BFF",
              color: "white",
              borderRadius: "8px",
              px: 2.5,
              "&:hover": { bgcolor: "#4F46E5" },
              "&.Mui-disabled": { bgcolor: "#C7D2FE", color: "white" },
            }}
          >
            Impersonate
          </Button>
          <Button
            onClick={() => setLockoutOpen(true)}
            sx={{
              textTransform: "none",
              border: "1px solid #E0E0E0",
              color: "#374151",
              borderRadius: "8px",
              px: 2.5,
            }}
          >
            Lockout override
          </Button>
        </div>
      </div>

      <div className="lg:flex lg:items-start lg:gap-6">
        <div className="min-w-0 flex-1 space-y-6">
      <div className="bg-white border border-gray-200 rounded-[10px] p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <MetaItem label="Status" value={listMeta?.subscriptionStatus} />
        <MetaItem
          label="Period end"
          value={formatPeriodEnd(listMeta?.subscriptionEndDate)}
        />
        <TrialEndItem
          orgId={orgId}
          trialExpiresAt={
            organization
              ? readTrialExpiresAt(organization)
              : navState.trialExpiresAt ?? null
          }
          isPaid={
            (unwrap(organization?.subscription_details?.M?.sub_status) ||
              listMeta?.subscriptionStatus) === "paid"
          }
          onSaved={(trialExpiresAt, subStatus) => {
            const reopened = subStatus === "trial";
            setOrganization((prev) => {
              if (!prev) return prev;
              const { stripe_sub, ...rest } = prev.subscription_details?.M || {};
              const sub = reopened ? rest : { ...rest, ...(stripe_sub ? { stripe_sub } : {}) };
              return {
                ...prev,
                subscription_details: {
                  M: {
                    ...sub,
                    trial_expires_at: { N: String(trialExpiresAt) },
                    sub_status: { S: subStatus },
                    plan: sub.plan || { S: "Team" },
                  },
                },
              };
            });
            setListMeta((prev) => ({
              ...prev,
              subscriptionStatus: subStatus,
              ...(reopened ? { stripeSubscriptionId: null } : {}),
            }));
          }}
        />
        <MetaItem
          label="Payment method"
          value={
            listMeta?.paymentMethodEnabled == null
              ? null
              : listMeta.paymentMethodEnabled
                ? "Enabled"
                : "Not enabled"
          }
        />
        <MetaItem label="Owner" value={ownerEmail || ownerUserId} />
        <MetaItem label="MRR" value={listMeta?.mrr != null ? `$${listMeta.mrr}` : null} />
        <MetaItem label="Stripe customer" value={listMeta?.stripeCustomerId} />
        <MetaItem label="Stripe subscription" value={listMeta?.stripeSubscriptionId} />
        <MetaItem
          label="Timezone"
          value={pickOrgField(organization, "org_timezone", "orgTimezone")}
        />
        <MetaItem
          label="Lockout override"
          value={lockoutOverrideLabel(readLockoutOverride(organization))}
        />
      </div>

      <section className="bg-white border border-gray-200 rounded-[10px] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-[15px] font-semibold">Call volume (14 days)</h2>
          <div
            className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 p-0.5"
            role="tablist"
            aria-label="Call volume series"
          >
            {CALL_SERIES.map((s) => {
              const active = callSeries === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setCallSeries(s.id)}
                  className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    active
                      ? "bg-white text-gray-900 shadow-sm"
                      : "text-gray-500 hover:text-gray-800"
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
        {chartData.length === 0 ? (
          <p className="text-[13px] text-gray-400">No call data</p>
        ) : (
          <div className="h-[220px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id={chartGradId} x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={activeCallSeries.color}
                      stopOpacity={0.35}
                    />
                    <stop
                      offset="95%"
                      stopColor={activeCallSeries.color}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={32} />
                <Tooltip />
                <Area
                  type="monotone"
                  dataKey={activeCallSeries.id}
                  stroke={activeCallSeries.color}
                  fill={`url(#${chartGradId})`}
                  strokeWidth={2}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-[10px] p-4">
        <h2 className="text-[15px] font-semibold mb-3">
          Users{users.length ? ` (${users.length})` : ""}
        </h2>
        {users.length === 0 ? (
          <p className="text-[13px] text-gray-400">No users</p>
        ) : (
          <div className="overflow-x-auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Name", "Email", "Role", "Status", "User ID"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, fontSize: 12, color: "#6B7280" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => {
                  const role = user.userType || user.type || "—";
                  const status = user.status || "—";
                  const statusLower = String(status).toLowerCase();
                  const statusClass =
                    statusLower === "active"
                      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                      : statusLower === "invited" || statusLower === "pending"
                        ? "bg-sky-50 text-sky-700 border-sky-200"
                        : statusLower === "disabled" ||
                            statusLower === "inactive" ||
                            statusLower === "removed"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-gray-50 text-gray-600 border-gray-200";
                  return (
                    <TableRow key={user.userId || user.email}>
                      <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                        {user.name || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {user.email || "—"}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        <span className="capitalize">{role}</span>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>
                        {status === "—" ? (
                          "—"
                        ) : (
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap capitalize ${statusClass}`}
                          >
                            {status}
                          </span>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>
                        {user.userId || "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-[10px] p-4">
        <h2 className="text-[15px] font-semibold mb-3">Routes</h2>
        {routes.length === 0 ? (
          <p className="text-[13px] text-gray-400">No routes</p>
        ) : (
          <div className="overflow-x-auto">
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Name", "ID", "Phone"].map((h) => (
                    <TableCell
                      key={h}
                      sx={{ fontWeight: 600, fontSize: 12, color: "#6B7280" }}
                    >
                      {h}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {routes.map((route, idx) => {
                  const id =
                    unwrap(route.route_id) ||
                    unwrap(route.routeId) ||
                    unwrap(route.id) ||
                    idx;
                  const name =
                    unwrap(route.route_name) ||
                    unwrap(route.name) ||
                    unwrap(route.routeName) ||
                    "—";
                  const phone =
                    unwrap(route.configured_phone) ||
                    unwrap(route.phone_number) ||
                    unwrap(route.phoneNumber) ||
                    unwrap(route.number) ||
                    "—";
                  return (
                    <TableRow
                      key={String(id)}
                      hover
                      sx={{ cursor: "pointer" }}
                      onClick={() => {
                        setSelectedRoute(route);
                        setRouteModalOpen(true);
                      }}
                    >
                      <TableCell sx={{ fontSize: 13 }}>{name}</TableCell>
                      <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>
                        {String(id)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 13 }}>{phone}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="bg-white border border-gray-200 rounded-[10px] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="text-[15px] font-semibold">Twilio</h2>
          <div className="flex flex-wrap gap-2">
            {!twilioLoaded && (
              <Button
                onClick={loadTwilio}
                disabled={twilioLoading}
                sx={{
                  textTransform: "none",
                  bgcolor: "#605BFF",
                  color: "white",
                  borderRadius: "8px",
                  px: 2,
                  "&:hover": { bgcolor: "#4F46E5" },
                }}
              >
                {twilioLoading ? (
                  <CircularProgress size={18} sx={{ color: "white" }} />
                ) : (
                  "Load Twilio configuration"
                )}
              </Button>
            )}
            {twilioLoaded && (
              <>
                <Button
                  onClick={loadTwilio}
                  disabled={twilioLoading}
                  sx={{
                    textTransform: "none",
                    border: "1px solid #E0E0E0",
                    color: "#374151",
                    borderRadius: "8px",
                  }}
                >
                  {twilioLoading ? (
                    <CircularProgress size={16} />
                  ) : (
                    "Refresh"
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setVoicePrefill({ phone: "", url: "" });
                    setVoiceOpen(true);
                  }}
                  sx={{
                    textTransform: "none",
                    border: "1px solid #E0E0E0",
                    color: "#374151",
                    borderRadius: "8px",
                  }}
                >
                  Update voice URL
                </Button>
                <Button
                  onClick={handleGenerateAppDetails}
                  disabled={genLoader}
                  sx={{
                    textTransform: "none",
                    border: "1px solid #E0E0E0",
                    color: "#374151",
                    borderRadius: "8px",
                  }}
                >
                  {genLoader ? (
                    <CircularProgress size={16} />
                  ) : (
                    "Generate application details"
                  )}
                </Button>
              </>
            )}
          </div>
        </div>

        {twilioError && (
          <div className="text-red-500 text-[13px] font-semibold mb-2">
            <ErrorOutline sx={{ fontSize: 16 }} /> {twilioError}
          </div>
        )}
        {genMsg && (
          <div className="text-[13px] text-gray-600 mb-2">{genMsg}</div>
        )}

        {!twilioLoaded && !twilioLoading && (
          <p className="text-[13px] text-gray-400">
            Twilio is loaded on demand (calls the customer Twilio API).
          </p>
        )}

        {twilioLoaded && !twilio && (
          <p className="text-[13px] text-gray-400">
            No Twilio configuration returned (missing credentials?).
          </p>
        )}

        {twilio && (
          <div className="space-y-4">
            {twilio.account_credentials_valid === false && (
              <div className="flex items-start gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-[13px]">
                <ErrorOutline sx={{ fontSize: 16, mt: "2px" }} />
                <div>
                  <span className="font-semibold">Account credentials invalid</span>
                  {twilioFetchMeta.accountFetchError
                    ? `: ${twilioFetchMeta.accountFetchError}`
                    : ""}
                </div>
              </div>
            )}
            {(twilioFetchMeta.appSidError ||
              twilioFetchMeta.apiSidError ||
              twilioFetchMeta.numbersError) && (
              <div className="space-y-1">
                {twilioFetchMeta.appSidError && (
                  <div className="text-amber-700 text-[13px]">
                    <ErrorOutline sx={{ fontSize: 14 }} /> App SID check:{" "}
                    {twilioFetchMeta.appSidError}
                  </div>
                )}
                {twilioFetchMeta.apiSidError && (
                  <div className="text-amber-700 text-[13px]">
                    <ErrorOutline sx={{ fontSize: 14 }} /> API SID check:{" "}
                    {twilioFetchMeta.apiSidError}
                  </div>
                )}
                {twilioFetchMeta.numbersError && (
                  <div className="text-amber-700 text-[13px]">
                    <ErrorOutline sx={{ fontSize: 14 }} /> Numbers:{" "}
                    {twilioFetchMeta.numbersError}
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <MetaItem
                label="Account SID"
                value={twilio.accountSid || twilio.account_sid}
                sidValid={twilio.account_credentials_valid}
              />
              <MetaItem
                label="App SID"
                value={twilio.appSid || twilio.app_sid}
                sidValid={twilio.app_sid_exists}
              />
              <MetaItem
                label="API SID"
                value={twilio.apiSid || twilio.api_sid}
                sidValid={twilio.api_sid_exists}
              />
              <MetaItem
                label="Trial"
                value={
                  (() => {
                    const trial = coerceBoolean(
                      twilio.onTwilioTrial ?? twilio.trial ?? twilio.isTrial
                    );
                    if (trial == null) return null;
                    return trial ? "Yes" : "No";
                  })()
                }
              />
              <MetaItem
                label="Push credential SID"
                value={
                  twilio.pushCredentialSid ||
                  twilio.push_credential_sid ||
                  twilio.push_credentials?.apn ||
                  twilio.push_credentials?.fcm ||
                  null
                }
              />
            </div>

            <div>
              <h3 className="text-[13px] font-semibold mb-2">Numbers</h3>
              {twilioNumbers.length === 0 ? (
                <p className="text-[13px] text-gray-400">No numbers</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {["Phone", "SID", "Voice URL", ""].map((h) => (
                          <TableCell
                            key={h || "actions"}
                            sx={{ fontWeight: 600, fontSize: 12, color: "#6B7280" }}
                          >
                            {h}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {twilioNumbers.map((num) => {
                        const phone =
                          num.phoneNumber || num.phone_number || num.number;
                        const voiceUrl = num.voiceUrl || num.voice_url || "";
                        return (
                          <TableRow key={num.sid || phone}>
                            <TableCell sx={{ fontSize: 13 }}>{phone || "—"}</TableCell>
                            <TableCell sx={{ fontSize: 12, color: "#6B7280" }}>
                              {num.sid || "—"}
                            </TableCell>
                            <TableCell
                              sx={{
                                fontSize: 12,
                                maxWidth: 280,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {voiceUrl || "—"}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="small"
                                onClick={() => {
                                  setVoicePrefill({
                                    phone: phone || "",
                                    url: voiceUrl || "",
                                  });
                                  setVoiceOpen(true);
                                }}
                                sx={{ textTransform: "none", fontSize: 12 }}
                              >
                                Edit URL
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </div>
        )}
      </section>
        </div>

        <aside className="mt-6 lg:mt-0 lg:w-[340px] lg:shrink-0 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
          <ActivityTimeline
            activity={activity}
            onAddNote={() => setNoteOpen(true)}
            hasMore={activityHasMore}
            loadingMore={activityLoadingMore}
            onLoadMore={loadMoreActivity}
          />
        </aside>
      </div>

      <AddActivityNoteModal
        open={noteOpen}
        setOpen={setNoteOpen}
        orgId={orgId}
        onCreated={refreshActivity}
      />
      <RouteDataModal
        open={routeModalOpen}
        setOpen={setRouteModalOpen}
        data={selectedRoute}
        rootLabel="route"
        title={
          selectedRoute
            ? unwrap(selectedRoute.name) ||
              unwrap(selectedRoute.route_name) ||
              unwrap(selectedRoute.routeName) ||
              "Route details"
            : "Route details"
        }
      />
      <RouteDataModal
        open={orgDataOpen}
        setOpen={setOrgDataOpen}
        data={organization}
        rootLabel="organization"
        title="Organization details"
      />
      <LockoutOverrideModal
        open={lockoutOpen}
        setOpen={setLockoutOpen}
        orgId={orgId}
        initialOverride={readLockoutOverride(organization)}
        onSaved={(override) => {
          setOrganization((prev) => ({
            ...(prev || {}),
            lockout_override: {
              M: {
                is_active: { BOOL: Boolean(override?.is_active) },
                ...(override?.expiration != null
                  ? { expiration: { N: String(override.expiration) } }
                  : {}),
                ...(override?.reason
                  ? { reason: { S: override.reason } }
                  : {}),
                ...(override?.lastUpdatedby
                  ? { lastUpdatedby: { S: override.lastUpdatedby } }
                  : {}),
              },
            },
          }));
          refreshActivity();
        }}
      />
      <ImpersonateModal
        open={impersonateOpen}
        setOpen={setImpersonateOpen}
        orgId={orgId}
        ownerEmail={ownerEmail}
        ownerUserId={ownerUserId}
        organizationName={orgName}
      />
      <UpdateVoiceUrlModal
        open={voiceOpen}
        setOpen={setVoiceOpen}
        orgId={orgId}
        initialPhone={voicePrefill.phone}
        initialUrl={voicePrefill.url}
        onUpdated={loadTwilio}
      />
    </div>
  );
};

export default AdminOrgDetail;
